// Vendor Billing v1.0 — the real cross-loan VFI ledger (replaces the
// Vendor Program v3.0 placeholder). Leads with summary chips (five-second
// rule), then the invoice table; the row drawer is deep-linkable via
// ?invoice= and the loan Vendors tab links here filtered via ?loan=.

import { useState } from "react";
import { Plus, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { formatCalendarDate, formatDateInTz, useDateFormatter } from "@/utils/datetime";
import {
  useVendorInvoices,
  useVfiScopedSummary,
} from "@/hooks/portfolioAdmin/useVendorBilling";
import type { VendorDto, VendorInvoiceDto } from "@/api/portfolioAdmin/types";
import { InvoiceStatusBadge } from "../billing/InvoiceStatusBadge";
import { IssueInvoiceModal } from "../billing/IssueInvoiceModal";
import { VendorInvoiceDrawer } from "../billing/VendorInvoiceDrawer";
import { fmtMoney } from "../billing/vendorBillingFormat";

interface VendorVfiLedgerTabProps {
  vendor: VendorDto;
}

// ONLY `dueDate` is a wall-calendar date here. This comment used to say "issue /
// due / paid", and that sentence is what put `issuedAt` on the UTC renderer:
// `VendorInvoiceService` stamps `IssuedAt` from the clock at both its writers, so
// an invoice issued after 5pm Phoenix rendered as TOMORROW.
//
// `paidAt` is deliberately left on the UTC renderer — it is MIXED at the data
// layer (Stripe settlement writes an instant, a manually recorded payment writes
// a picked day) and neither renderer is right for every row. See the note on the
// same field in `VendorInvoiceDrawer`.
const calendarDate = (iso: string) => formatCalendarDate(iso);

export function VendorVfiLedgerTab({ vendor }: VendorVfiLedgerTabProps) {
  const { tz } = useDateFormatter();
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const loanFilter = params.get("loan");
  const openInvoiceId = params.get("invoice");
  const [issueOpen, setIssueOpen] = useState(false);

  // D3 — the vendor's own ledger shows the vendor's own invoices. A regulated
  // vendor's borrower-billed fees are Phoenix billing the BORROWER; they used
  // to be listed here and counted into this vendor's totals.
  const invoicesQuery = useVendorInvoices({
    vendorId: vendor.id,
    loanId: loanFilter ?? undefined,
    audience: "vendor",
  });
  const borrowerBilledQuery = useVendorInvoices({
    vendorId: vendor.id,
    loanId: loanFilter ?? undefined,
    audience: "borrower",
  });
  // Figures come from the server so this tab, the loan card and the registry
  // dashboard cannot disagree on what "open" means or where the Paid-YTD year
  // starts. Scoped to the vendor; the ?loan= filter narrows the TABLE only.
  const summaryQuery = useVfiScopedSummary({ vendorId: vendor.id });
  const rows = invoicesQuery.data ?? [];
  const borrowerRows = borrowerBilledQuery.data ?? [];

  const canIssue = auth.hasPermission("VendorBilling.Issue");

  const setParam = (key: string, value: string | null) => {
    const updated = new URLSearchParams(params);
    if (value) updated.set(key, value);
    else updated.delete(key);
    setParams(updated, { replace: true });
  };

  const summary = summaryQuery.data;

  if (invoicesQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading VFI ledger…</p>
      </Card>
    );
  }
  if (invoicesQuery.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0">
          Could not load the VFI ledger. Refresh to retry.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="vendor-vfi-ledger-tab">
      {summary && (
        <section
          className="grid grid-cols-1 md:grid-cols-4 gap-space-3"
          data-testid="vfi-ledger-summary"
        >
          <SummaryStat
            label="Open invoices"
            value={fmtMoney(summary.openTotal)}
            detail={`${summary.openCount} open`}
          />
          <SummaryStat
            label="Overdue"
            value={fmtMoney(summary.overdueTotal)}
            detail={`${summary.overdueCount} overdue`}
            tone={summary.overdueCount > 0 ? "error" : undefined}
          />
          {/* Carved out of open, never dropped — a settling ACH payment is
              money on its way and needs no action, but it is still money. */}
          <SummaryStat
            label="In transit"
            value={fmtMoney(summary.inTransitTotal)}
            detail={`${summary.inTransitCount} settling`}
          />
          <SummaryStat label="Paid YTD" value={fmtMoney(summary.paidYtdTotal)} />
        </section>
      )}

      <Card>
        <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-3">
          <div className="flex items-center gap-space-2">
            <h3 className="font-display font-bold text-h3 m-0">VFI invoices</h3>
            {loanFilter && (
              <button
                type="button"
                className="inline-flex items-center gap-space-1 rounded-pill border border-border-light bg-white-warm px-space-2 py-[2px] text-caption text-gray-mid hover:text-onyx"
                onClick={() => setParam("loan", null)}
                data-testid="vfi-ledger-loan-filter"
              >
                Filtered to one loan <X size={12} aria-hidden />
              </button>
            )}
          </div>
          {canIssue && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setIssueOpen(true)}
              data-testid="vfi-issue-invoice-button"
            >
              <Plus size={14} aria-hidden /> Issue invoice
            </Button>
          )}
        </div>

        {rows.length === 0 ? (
          <div className="flex flex-col items-start gap-space-2">
            <p className="text-small text-gray-mid m-0">
              No VFI invoices for this vendor{loanFilter ? " on this loan" : ""} yet.
            </p>
            {canIssue && (
              <Button variant="secondary" size="sm" onClick={() => setIssueOpen(true)}>
                Issue the first invoice
              </Button>
            )}
          </div>
        ) : (
          <Table data-testid="vfi-ledger-table">
            <THead>
              <TR>
                <TH className="text-white-pure">Invoice</TH>
                <TH className="text-white-pure">Loan</TH>
                <TH className="text-white-pure text-right">Amount</TH>
                <TH className="text-white-pure">Issued</TH>
                <TH className="text-white-pure">Due</TH>
                <TH className="text-white-pure">Status</TH>
                <TH className="text-white-pure">Paid</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((invoice: VendorInvoiceDto) => (
                <TR
                  key={invoice.id}
                  onClick={() => setParam("invoice", invoice.id)}
                  className="cursor-pointer"
                  data-testid={`vfi-invoice-row-${invoice.id}`}
                >
                  <TD className="font-medium">{invoice.invoiceNumber}</TD>
                  <TD>{invoice.loanNumber}</TD>
                  <TD className="text-right tabular-nums">{fmtMoney(invoice.amount)}</TD>
                  <TD>{invoice.issuedAt ? formatDateInTz(invoice.issuedAt, tz) : "—"}</TD>
                  <TD>{invoice.dueDate ? calendarDate(invoice.dueDate) : "—"}</TD>
                  <TD><InvoiceStatusBadge status={invoice.status} /></TD>
                  <TD>{invoice.paidAt ? calendarDate(invoice.paidAt) : "—"}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* D3 — borrower-billed (regulated-vendor) fees: Phoenix bills the
          BORROWER, so these are not this vendor's receivables and are kept out
          of every figure above. They are still real money on this vendor's
          engagements, so they are shown here under their own label rather than
          merged into the vendor's totals or hidden entirely. */}
      {borrowerRows.length > 0 && (
        <Card data-testid="vfi-ledger-borrower-billed">
          <div className="flex items-baseline justify-between gap-space-3 flex-wrap mb-space-2">
            <h3 className="font-display font-bold text-h3 m-0">
              Billed to borrowers
            </h3>
            {summary && (
              <span className="text-small text-gray-mid">
                {summary.borrowerBilledOpenCount} open ·{" "}
                {fmtMoney(summary.borrowerBilledOpenTotal)}
              </span>
            )}
          </div>
          <p className="text-small text-gray-mid m-0 mb-space-3">
            This vendor is regulated — Phoenix bills the borrower for these
            fees, so they are not counted in the figures above.
          </p>
          <Table data-testid="vfi-borrower-billed-table">
            <THead>
              <TR>
                <TH className="text-white-pure">Invoice</TH>
                <TH className="text-white-pure">Loan</TH>
                <TH className="text-white-pure text-right">Amount</TH>
                <TH className="text-white-pure">Due</TH>
                <TH className="text-white-pure">Status</TH>
              </TR>
            </THead>
            <TBody>
              {borrowerRows.map((invoice: VendorInvoiceDto) => (
                <TR
                  key={invoice.id}
                  onClick={() => setParam("invoice", invoice.id)}
                  className="cursor-pointer"
                  data-testid={`vfi-borrower-invoice-row-${invoice.id}`}
                >
                  <TD className="font-medium">{invoice.invoiceNumber}</TD>
                  <TD>{invoice.loanNumber}</TD>
                  <TD className="text-right tabular-nums">{fmtMoney(invoice.amount)}</TD>
                  <TD>{invoice.dueDate ? calendarDate(invoice.dueDate) : "—"}</TD>
                  <TD><InvoiceStatusBadge status={invoice.status} /></TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      {openInvoiceId && (
        <VendorInvoiceDrawer
          invoiceId={openInvoiceId}
          open={!!openInvoiceId}
          onClose={() => setParam("invoice", null)}
        />
      )}
      {issueOpen && (
        <IssueInvoiceModal
          open={issueOpen}
          onClose={() => setIssueOpen(false)}
          vendorId={vendor.id}
          vendorName={vendor.name}
        />
      )}
    </div>
  );
}

function SummaryStat({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: "error";
}) {
  return (
    <Card className="flex flex-col gap-space-1">
      <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
        {label}
      </span>
      <span
        className={`font-display font-bold text-h2 tabular-nums ${
          tone === "error" ? "text-status-error" : ""
        }`}
      >
        {value}
      </span>
      {detail ? <span className="text-small text-gray-mid">{detail}</span> : null}
    </Card>
  );
}
